import React, { useState, useEffect, useRef } from "react";
import { LogOut, MessageSquare, Send, Shield, Clock, CheckCircle } from "react-feather";
import { logAttendanceHeartbeat } from "shared/utils/firebase";
import styles from "./EventLiveRoomModal.module.css";

const EventLiveRoomModal = ({ event, currentUser, onClose }) => {
  const [activeMinutes, setActiveMinutes] = useState(1);
  const [chatMessage, setChatMessage] = useState("");
  const [chatList, setChatList] = useState([
    { id: 1, user: "Host / Admin", text: `Host live room session for ${event?.title || 'Event'} is active!`, time: "Just now" },
    { id: 2, user: "System", text: "Automated attendance tracking is active for participants.", time: "Just now" }
  ]);

  const heartbeatIntervalRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.uid || !event?.id) return;

    logAttendanceHeartbeat(currentUser.uid, event.id, event.duration || 60);

    heartbeatIntervalRef.current = setInterval(() => {
      setActiveMinutes((prev) => {
        const next = prev + 1;
        logAttendanceHeartbeat(currentUser.uid, event.id, event.duration || 60);
        return next;
      });
    }, 60000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [currentUser, event]);

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const newMsg = {
      id: Date.now(),
      user: currentUser?.name || currentUser?.displayName || "Admin Host",
      text: chatMessage.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setChatList((prev) => [...prev, newMsg]);
    setChatMessage("");
  };

  if (!event) return null;

  // Clean industry standard Zoom/Meet URL bypassing lobby & directly opening centered video room
  const sanitizedRoomName = (event.id || event.title || "LiveSession").replace(/[^a-zA-Z0-9]/g, "_");
  const userName = currentUser?.name || currentUser?.displayName || "Admin Host";
  const embedUrl = `https://meet.jit.si/EventRoom_${sanitizedRoomName}#config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false&userInfo.displayName=${encodeURIComponent(userName)}`;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.liveBadgeGreen}>🟢 HOST LIVE ROOM</span>
            <h3 className={styles.title}>{event.title}</h3>
            <span className={styles.eventIdBadge}>
              ID: {event.id || "EV-849201"}
            </span>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.timerBadge}>
              <Clock size={14} style={{ color: '#38bdf8' }} />
              Host Session: <strong style={{ color: '#ffffff' }}>{activeMinutes} mins</strong>
            </span>
            <button className={styles.leaveBtn} onClick={onClose} title="End Host Meeting Session">
              <LogOut size={15} /> End Session
            </button>
          </div>
        </div>

        {/* Main Layout Grid: Video Stream + Live Q&A */}
        <div className={styles.mainGrid}>
          {/* Embedded Video Call Room */}
          <div className={styles.videoSection}>
            <iframe
              src={embedUrl}
              title={event.title}
              className={styles.videoFrame}
              allow="camera; microphone; display-capture; autoplay; clipboard-write; fullscreen"
            />
          </div>

          {/* Live Discussion & Q&A Sidebar */}
          <div className={styles.chatSidebar}>
            <div className={styles.chatHeader}>
              <MessageSquare size={16} style={{ color: '#38bdf8' }} />
              <span>Live Discussion & Q&A</span>
            </div>

            <div className={styles.chatMessages}>
              {chatList.map((msg) => (
                <div key={msg.id} className={styles.chatBubble}>
                  <div className={styles.chatUser}>
                    <span>{msg.user}</span>
                    <span className={styles.chatTime}>{msg.time}</span>
                  </div>
                  <div className={styles.chatText}>{msg.text}</div>
                </div>
              ))}
            </div>

            <form className={styles.chatInputRow} onSubmit={handleSendChat}>
              <input
                type="text"
                className={styles.chatInput}
                placeholder="Broadcast host message..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
              />
              <button type="submit" className={styles.chatSendBtn} title="Send Message">
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Attendance Tracking Footer */}
        <div className={styles.attendanceBar}>
          <div className={styles.attendanceLeft}>
            <Shield size={16} style={{ color: '#10b981' }} />
            <span>
              Host Room Status: <strong>ACTIVE</strong> (Students tracking automatically in real-time)
            </span>
          </div>
          <div className={styles.attendanceRight}>
            <CheckCircle size={15} style={{ color: '#10b981' }} />
            <span>SiliconLogix Native Live Room Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventLiveRoomModal;
